'use client';
import React, { ComponentType, useEffect, useRef, useState } from 'react';
import useIntersectionObserver from '../use-intersection-observer/intersection-observer';

// inspired by https://medium.com/@satyam-dua/lazy-loading-components-in-next-js-with-intersection-observer-and-typescript-9e96f69fa171
export const withLazyLoad = <P extends object>(WrappedComponent: ComponentType<P>, threshold = 0.5): React.FC<P> => {
    // For React 19 compatibility, ensure the wrapper is properly memoized
    const LazyComponent = React.memo((props: P) => {
        // Use React 18+ concurrent features safely
        const [isInView, ref] = useIntersectionObserver(threshold);
        const [hasLoaded, setHasLoaded] = useState(false);
        const hasLoadedRef = useRef(false);

        // Add a safety fallback to ensure visibility even if observer fails
        useEffect(() => {
            // Set a safety timeout to make components visible after 2 seconds
            // This ensures they appear even if intersection observer fails
            const safetyTimeout = setTimeout(() => {
                if (!hasLoadedRef.current) {
                    setHasLoaded(true);
                    hasLoadedRef.current = true;
                }
            }, 2000);

            return () => clearTimeout(safetyTimeout);
        }, []);

        // Handle intersection observer result in effect
        useEffect(() => {
            if (isInView && !hasLoadedRef.current) {
                hasLoadedRef.current = true;
                // eslint-disable-next-line react-hooks/set-state-in-effect
                setHasLoaded(true);
            }
        }, [isInView]);

        return <div ref={ref}>{hasLoaded || isInView ? <WrappedComponent {...props} /> : null}</div>;
    });

    // Set display name for debugging
    LazyComponent.displayName = `withLazyLoad(${WrappedComponent.displayName || WrappedComponent.name || 'Component'})`;

    return LazyComponent;
};

export default withLazyLoad;
